import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import {
  routes,
  type PimTargetRecoveryPreview,
  type PimTargetRecoveryReadback,
} from "@nova-ai/shared"
import { z } from "zod"
import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"
import {
  useNutritionRecoveryVerification,
  type NutritionRecoverySession,
} from "./use-nutrition-recovery-verification"

export function useNutritionRecovery(
  targetIds: number[],
  existingIds: string[],
  paused: boolean,
  session: NutritionRecoverySession
) {
  const client = useQueryClient()
  const verification = useNutritionRecoveryVerification(session, targetIds, paused)
  const preview = useMutation({
    mutationFn: async () => {
      const verificationRevision = verification.getPreviewRevision()
      const response = await apiFetch<PimTargetRecoveryPreview>(
        routes.pimEvents.recoveryPreview(),
        { method: "POST", body: { targetIds } }
      )
      if (!response.ok) {
        throw new Error(response.error.message)
      }
      return { ...response.value, verificationRevision }
    },
  })
  const execute = useMutation({
    mutationFn: async () => {
      const targets =
        preview.data?.targets
          .filter(
            (target) =>
              target.classification !== "unavailable" &&
              target.affectedScopes.length > 0 &&
              !target.resolved &&
              !target.recoveryId
          )
          .map(({ targetId, evidenceHash }) => ({ targetId, evidenceHash })) ?? []
      if (!targets.length) {
        throw new Error("Preview eligible targets before starting recovery.")
      }
      const response = await apiFetch<{ recoveries: PimTargetRecoveryReadback[] }>(
        routes.pimEvents.recoveryExecute(),
        { method: "POST", body: { targets } }
      )
      if (!response.ok) {
        // A later target may conflict after an earlier admission. Keep every returned admission.
        const partial = z
          .object({ recoveries: z.array(z.object({ id: z.string().uuid() })) })
          .safeParse(response.error.details)
        if (partial.success) {
          verification.authorize(partial.data.recoveries.map((recovery) => recovery.id))
        }
        throw new Error(response.error.message)
      }
      for (const recovery of response.value.recoveries) {
        client.setQueryData(queryKeys.nutritionRecovery(recovery.id), recovery)
      }
      verification.authorize(
        response.value.recoveries
          .filter((recovery) => recovery.state !== "resolved" && recovery.state !== "blocked")
          .map((recovery) => recovery.id)
      )
      return response.value
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["nutrition-queue"] })
      void client.invalidateQueries({ queryKey: ["nutrition-queue-detail"] })
    },
  })
  const recoveryIds = [
    ...new Set([
      ...existingIds,
      ...verification.sessions.map((item) => item.id),
      ...(execute.data?.recoveries.map((item) => item.id) ?? []),
    ]),
  ]
  const recoveries = useQueries({
    queries: recoveryIds.map((id) => ({
      queryKey: queryKeys.nutritionRecovery(id),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const response = await apiFetch<PimTargetRecoveryReadback>(
          routes.pimEvents.recoveryDetail({ recoveryId: id }),
          { signal }
        )
        if (!response.ok) {
          throw new Error(response.error.message)
        }
        return response.value
      },
      enabled: !paused,
      refetchOnWindowFocus: false,
      refetchInterval: (query: { state: { data: PimTargetRecoveryReadback | undefined } }) =>
        paused || query.state.data?.state === "resolved" ? false : 15000,
      refetchIntervalInBackground: false,
      retry: 1,
    })),
  })
  return { preview, execute, recoveries, recoveryIds, verification }
}
